import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { batchApi } from '../../api/batchApi'

// Async thunks
export const fetchTeacherBatches = createAsyncThunk(
  'batch/fetchTeacherBatches',
  async (params, { rejectWithValue }) => {
    try {
      const response = await batchApi.getTeacherBatches(params)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch batches'
      )
    }
  }
)

export const fetchBatchDetails = createAsyncThunk(
  'batch/fetchBatchDetails',
  async (batchId, { rejectWithValue }) => {
    try {
      const response = await batchApi.getBatchDetails(batchId)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch batch details'
      )
    }
  }
)

export const updateBatch = createAsyncThunk(
  'batch/updateBatch',
  async ({ batchId, data }, { rejectWithValue }) => {
    try {
      const response = await batchApi.updateBatch(batchId, data)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update batch'
      )
    }
  }
)

export const fetchBatchStudents = createAsyncThunk(
  'batch/fetchBatchStudents',
  async (batchId, { rejectWithValue }) => {
    try {
      const response = await batchApi.getBatchStudents(batchId)
      return response
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch batch students'
      )
    }
  }
)

const initialState = {
  batches: [],
  currentBatch: null,
  batchStudents: [],
  isLoading: false,
  error: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
  },
}

const batchSlice = createSlice({
  name: 'batch',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentBatch: (state) => {
      state.currentBatch = null
      state.batchStudents = []
    },
    resetBatchState: (state) => {
      state.batches = []
      state.currentBatch = null
      state.batchStudents = []
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Teacher Batches
      .addCase(fetchTeacherBatches.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTeacherBatches.fulfilled, (state, action) => {
        state.isLoading = false
        state.batches = action.payload.items || action.payload
        state.pagination = {
          page: action.payload.page || 1,
          limit: action.payload.limit || 10,
          total: action.payload.total || 0,
        }
      })
      .addCase(fetchTeacherBatches.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Batch Details
      .addCase(fetchBatchDetails.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBatchDetails.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentBatch = action.payload
      })
      .addCase(fetchBatchDetails.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Update Batch
      .addCase(updateBatch.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateBatch.fulfilled, (state, action) => {
        state.isLoading = false
        state.currentBatch = action.payload
        // Update in batches list if exists
        const index = state.batches.findIndex(b => b._id === action.payload._id)
        if (index !== -1) {
          state.batches[index] = action.payload
        }
      })
      .addCase(updateBatch.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })

      // Fetch Batch Students
      .addCase(fetchBatchStudents.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBatchStudents.fulfilled, (state, action) => {
        state.isLoading = false
        state.batchStudents = action.payload
      })
      .addCase(fetchBatchStudents.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
  },
})

export const { clearError, clearCurrentBatch, resetBatchState } = batchSlice.actions
export default batchSlice.reducer